import React, { useEffect, useState } from 'react';
import useCircleLevels from '../../hooks/useCircleLevels';
import useKPIs from '../../hooks/useKPIs';
import useLeaderboardViews from '../../hooks/useLeaderboardViews';
import {
  ViewContainer,
  Title,
  BreadcrumbContainer,
  TitleContainer,
  LoadingContainer,
} from './Leaderboard.styles';
import { createNewViewStask } from './Leaderboard.utils';
import useLeaderboardStore from '../../store/zustand/useLeaderboardStore';
import LeaderboardTable from './components/LeaderboardTable';
import { Button, CircularProgress, useTheme } from '@mui/material';
import LeaderboardSkeleton from './components/LeaderboardSkeleton';
import StackBreadCrumb from './components/StackBreadCrumb';
import LeaderboardPagination from './components/LeaderboardPagination';
import { GamifyIcon } from '../../components/GamifyIcon';
import DownloadCSVButton from './components/DownloadCSVButton';
import LeaderboardFilters from './components/LeaderboardFilters';
import LimitMenu from './components/LimitMenu';

const Leaderboard = () => {
  const { circleLevels } = useCircleLevels();
  const { KPIs } = useKPIs();
  const { leaderboardViews, defaultView } = useLeaderboardViews();
  const {
    setViewsStack,
    leaderboardFilters,
    viewsStack,
    updateFilters,
    isLoading,
    leaderboardData,
    applyView,
  } = useLeaderboardStore();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLimitChange = (limit: number) => {
    updateFilters({ limit });
    handleClose();
  };

  useEffect(() => {
    const initialAssetsLoaded =
      leaderboardViews && KPIs && circleLevels && circleLevels.length > 0;
    if (!initialAssetsLoaded) {
      return;
    }

    if (defaultView) {
      applyView(defaultView.id);
    } else {
      setViewsStack(createNewViewStask(circleLevels[0]));
    }
  }, [leaderboardViews, KPIs, circleLevels]);

  if (
    !(leaderboardViews && KPIs && circleLevels && circleLevels.length > 0) ||
    !leaderboardData
  ) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} sx={{ color: '#ff6a00' }} />
      </LoadingContainer>
    );
  }

  return (
    <ViewContainer>
      <TitleContainer>
        <Title>Leaderboard</Title>
        <DownloadCSVButton />
      </TitleContainer>
      <LeaderboardFilters />
      <BreadcrumbContainer>
        <StackBreadCrumb
          viewsStack={viewsStack}
          onStackClick={(index) => {
            setViewsStack(viewsStack.slice(0, index + 1));
          }}
        />
        <Button onClick={handleClick}>
          <GamifyIcon icon="settings" color={theme.custom.text} />
        </Button>
        <LimitMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onLimitChange={handleLimitChange}
          currentLimit={leaderboardFilters?.limit || 10}
        />
      </BreadcrumbContainer>
      {isLoading ? (
        <LeaderboardSkeleton />
      ) : (
        <>
          {leaderboardData?.results && <LeaderboardTable />}
          {leaderboardData?.nextTokensList && (
            <LeaderboardPagination
              nextTokensList={leaderboardData.nextTokensList}
              currentToken={leaderboardFilters?.nextToken || ''}
              onPageChange={(nextToken) => updateFilters({ nextToken })}
            />
          )}
        </>
      )}
    </ViewContainer>
  );
};

export default Leaderboard;
